import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Row, Col, Form, Button, Spinner, ListGroup, Modal, InputGroup, Dropdown } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import { callAgentSendMessage, callAgentGenerateTitle, callSubmitLead, callCancelLeadHandoff } from '../utils/firebase';
import { createSession, saveMessage, updateMessage, updateSessionTitle, uploadImage, uploadFile, saveAttachment, appendUserChatEntry, subscribeToMessages, subscribeToChatHistory, subscribeToAttachments, subscribeToStreamingResponse, subscribeToAgentStatus, subscribeToAgentProcessing, setAgentProcessing, clearAgentProcessing } from '../utils/firebase';
import { trackEvent, trackFirstTime, trackFunnelEvent } from '../utils/analytics';
import { logError } from '../utils/errorLogger';
import DynamicForm from './DynamicForm';
import LeadHandoffCard from './LeadHandoffCard';
import SocialShareDialog from './SocialShareDialog';

/**
 * Convert chatHistory (Gemini Content[]) into display messages for the UI.
 * Filters out internal tool calls, keeps user text, assistant text,
 * ask_user questions, show_form forms, update_todo progress, and generated images.
 */
function chatHistoryToDisplayMessages(chatHistory, attachmentsMap = {}) {
  if (!chatHistory || !Array.isArray(chatHistory)) return [];

  const displayMessages = [];
  // Track generated images from non-displayed model entries so they can be
  // attached to the next displayed assistant message (e.g. when the annotated
  // entry also contains internal tool calls and gets filtered out).
  let pendingGeneratedImages = null;

  // Scan once for a resolved request_lead_handoff response so the card UI
  // can render its submitted/skipped state instead of an editable form.
  // The agent calls request_lead_handoff at most once per session.
  let leadHandoffOutcome = null;
  for (const entry of chatHistory) {
    if (entry?.role !== 'user' || !Array.isArray(entry.parts)) continue;
    const fr = entry.parts.find(p => p.functionResponse && p.functionResponse.name === 'request_lead_handoff');
    if (!fr) continue;
    const resp = fr.functionResponse.response || {};
    if (resp.submitted === true || resp.status === 'captured') {
      leadHandoffOutcome = 'captured';
    } else {
      leadHandoffOutcome = 'skipped';
    }
    break;
  }

  for (const entry of chatHistory) {
    if (!entry || !entry.parts) continue;

    if (entry.role === 'user') {
      // User text messages (not function responses)
      const textParts = entry.parts.filter(p => p.text && !p.functionResponse);
      const hasOnlyFunctionResponses = entry.parts.every(p => p.functionResponse);
      // Function-response-only entries are usually internal (e.g. synthetic
      // responses to ask_user), but when the client attached a `_displayText`
      // (form submissions, etc.) the server carries it onto the synthesized
      // entry so we can still render a human-readable user bubble.
      if (hasOnlyFunctionResponses) {
        if (entry._displayText || (entry._attachmentIds && entry._attachmentIds.length > 0)) {
          let images;
          if (entry._attachmentIds && entry._attachmentIds.length > 0) {
            const resolved = entry._attachmentIds
              .map(id => attachmentsMap[id])
              .filter(Boolean)
              .map(att => ({
                url: att.url,
                name: att.name,
                kind: att.kind,
                type: att.kind === 'pdf' ? 'application/pdf' : (att.kind === 'link' ? 'link' : att.mimeType),
                label: att.label || null,
                title: att.extracted?.title || null,
              }));
            if (resolved.length > 0) images = JSON.stringify(resolved);
          }
          displayMessages.push({
            role: 'user',
            content: entry._displayText || '',
            images,
          });
        }
        continue;
      }

      if (textParts.length > 0) {
        // Use _displayText if available (e.g. form submissions store a
        // human-friendly version separate from the agent-facing JSON).
        const text = entry._displayText || textParts.map(p => p.text).join('\n');
        // Resolve attachment display data: prefer _attachmentIds → attachmentsMap, fall back to _images
        let images;
        if (entry._attachmentIds && entry._attachmentIds.length > 0) {
          const resolved = entry._attachmentIds
            .map(id => attachmentsMap[id])
            .filter(Boolean)
            .map(att => ({
              url: att.url,
              name: att.name,
              kind: att.kind,
              type: att.kind === 'pdf' ? 'application/pdf' : (att.kind === 'link' ? 'link' : att.mimeType),
              label: att.label || null,
              title: att.extracted?.title || null,
            }));
          if (resolved.length > 0) images = JSON.stringify(resolved);
        } else if (entry._images) {
          images = JSON.stringify(entry._images);
        }
        // Skip form submission messages that start with "Form submission for"
        // as they are internal agent protocol
        displayMessages.push({
          role: 'user',
          content: text,
          images,
        });
      }
    } else if (entry.role === 'model') {
      // Check for text parts
      const textParts = entry.parts.filter(p => p.text);
      const functionCalls = entry.parts.filter(p => p.functionCall);

      // Show text from model entries that are "final" for a turn:
      // - No function calls (pure text response)
      // - Contains ONLY ask_user or show_form (the agent loop exits on these,
      //   so this entry IS the final response — no follow-up entry will exist)
      // Hide text from entries with internal tool calls (update_todo,
      // add_work_item, etc.) — these are intermediate and the loop continues.
      // Also hide text from "mixed batch" entries where ask_user/show_form was
      // called alongside internal tools — the server rejects the interaction
      // call and asks the model to re-issue it alone, so these are NOT final.
      const userFacingCalls = ['ask_user', 'show_form', 'request_lead_handoff'];
      const hasUserFacingCall = functionCalls.some(p =>
        userFacingCalls.includes(p.functionCall.name));
      const hasInternalCall = functionCalls.some(p =>
        !userFacingCalls.includes(p.functionCall.name));
      // Only treat as final if user-facing calls are standalone (no internal tools mixed in)
      const isStandaloneUserFacing = hasUserFacingCall && !hasInternalCall;

      // Collect generated images from this entry (displayed or not)
      if (entry._generatedImages) {
        pendingGeneratedImages = [
          ...(pendingGeneratedImages || []),
          ...entry._generatedImages,
        ];
      }

      if (textParts.length > 0 && (functionCalls.length === 0 || isStandaloneUserFacing)) {
        const text = textParts.map(p => p.text).join('\n');
        if (text.trim()) {
          const msg = { role: 'assistant', content: text };
          if (pendingGeneratedImages) {
            msg.generatedImages = JSON.stringify(pendingGeneratedImages);
            pendingGeneratedImages = null;
          }
          displayMessages.push(msg);
        }
      }

      // Show specific function calls as UI elements.
      // Skip user-facing calls (ask_user, show_form) from "mixed batch" entries
      // where they were called alongside internal tools — the server rejects
      // these and the model re-issues them alone on a subsequent turn.
      for (const part of functionCalls) {
        const call = part.functionCall;
        if (!call) continue;

        if (call.name === 'ask_user') {
          // Show ask_user as assistant message (if not already covered by text)
          // Only from standalone entries — skip mixed batches (rejected by server)
          if (!hasInternalCall && textParts.length === 0 && call.args?.question) {
            const askMsg = { role: 'assistant', content: call.args.question };
            if (pendingGeneratedImages) {
              askMsg.generatedImages = JSON.stringify(pendingGeneratedImages);
              pendingGeneratedImages = null;
            }
            displayMessages.push(askMsg);
          }
        } else if (call.name === 'request_lead_handoff') {
          // Lead-capture card — only from standalone entries (not mixed batches)
          if (!hasInternalCall) {
            displayMessages.push({
              role: 'lead_handoff',
              content: JSON.stringify(call.args || {}),
              submittedState: leadHandoffOutcome,
            });
          }
        } else if (call.name === 'show_form') {
          // Show form UI — only from standalone entries (not mixed batches)
          if (!hasInternalCall) {
            // Flush pending generated images as a standalone message before the
            // form so they appear in the correct chronological position (after
            // the user's message, before the form).
            if (pendingGeneratedImages) {
              displayMessages.push({
                role: 'assistant',
                content: '',
                generatedImages: JSON.stringify(pendingGeneratedImages),
              });
              pendingGeneratedImages = null;
            }
            displayMessages.push({
              role: 'dynamic_form',
              content: JSON.stringify(call.args),
            });
          }
        } else if (call.name === 'update_todo') {
          // Show todo update
          if (call.args?.items) {
            displayMessages.push({
              role: 'todo_update',
              content: JSON.stringify(call.args.items),
            });
          }
        }
        // All other function calls are hidden
      }
    }
  }

  // If there are still pending generated images (the annotated entry wasn't
  // displayed), attach them to the last displayed assistant message.
  // If no assistant message exists, create a standalone image message so
  // generated designs are never silently dropped.
  if (pendingGeneratedImages) {
    let attached = false;
    for (let i = displayMessages.length - 1; i >= 0; i--) {
      if (displayMessages[i].role === 'assistant') {
        const existing = displayMessages[i].generatedImages
          ? safeJsonParse(displayMessages[i].generatedImages, [])
          : [];
        displayMessages[i].generatedImages = JSON.stringify([
          ...existing,
          ...pendingGeneratedImages,
        ]);
        attached = true;
        pendingGeneratedImages = null;
        break;
      }
    }
    // Fallback: no assistant message to attach to — create a standalone message
    // so the generated design images are always visible to the user.
    if (!attached) {
      displayMessages.push({
        role: 'assistant',
        content: '',
        generatedImages: JSON.stringify(pendingGeneratedImages),
      });
      pendingGeneratedImages = null;
    }
  }

  return displayMessages;
}

// Safe JSON parse that returns a fallback instead of throwing during render
function safeJsonParse(str, fallback = null) {
  if (typeof str !== 'string') return str ?? fallback;
  try {
    return JSON.parse(str);
  } catch (e) {
    console.error('[ChatInterface] JSON parse error:', e.message, 'input:', str?.substring?.(0, 100));
    return fallback;
  }
}

// Component to render a todo list update as a chat message
function TodoUpdateMessage({ items }) {
  if (!Array.isArray(items)) return null;
  const doneCount = items.filter((t) => t.status === 'done').length;
  const totalCount = items.length;

  const statusIcon = (status) => {
    switch (status) {
      case 'done':
        return <i className="bi bi-check-circle-fill text-success me-2"></i>;
      case 'in_progress':
        return <i className="bi bi-arrow-right-circle-fill text-primary me-2"></i>;
      default:
        return <i className="bi bi-circle text-muted me-2"></i>;
    }
  };

  return (
    <div className="todo-update-message">
      <div className="todo-update-header d-flex align-items-center mb-2">
        <i className="bi bi-list-check me-2"></i>
        <span className="fw-semibold">Consultation Progress</span>
        <span className="badge bg-secondary ms-auto">{doneCount}/{totalCount}</span>
      </div>
      <ListGroup variant="flush" className="todo-update-list">
        {items.map((item, index) => (
          <ListGroup.Item
            key={index}
            className={`todo-update-item py-1 px-0 border-0 ${item.status}`}
          >
            <div className="d-flex align-items-start">
              {statusIcon(item.status)}
              <span className={item.status === 'done' ? 'text-decoration-line-through text-muted' : ''}>
                {item.task}
              </span>
            </div>
          </ListGroup.Item>
        ))}
      </ListGroup>
    </div>
  );
}

/**
 * Debug log view — renders the raw chatHistory as a sequential log
 * showing all entries including tool calls and function responses.
 * Excludes system prompts. Visible only to dev admins.
 */
function DebugLogView({ chatHistory }) {
  const [copied, setCopied] = useState(false);

  const handleCopyAll = () => {
    if (!chatHistory || chatHistory.length === 0) return;
    const lines = [];
    for (const entry of chatHistory) {
      if (!entry) continue;
      const role = (entry.role || 'unknown').toUpperCase();
      const meta = [];
      if (entry._agent) meta.push(`agent: ${entry._agent}`);
      if (entry._routing) meta.push(`routed: ${entry._routing.reason}`);
      lines.push(`--- ${role} #${chatHistory.indexOf(entry)}${meta.length ? ' (' + meta.join(', ') + ')' : ''} ---`);
      if (entry._routing) lines.push(`  task: ${entry._routing.task}`);
      for (const part of (entry.parts || [])) {
        if (part.text) {
          lines.push(`[text]\n${part.text}`);
        } else if (part.functionCall) {
          lines.push(`[functionCall] ${part.functionCall.name}`);
          if (part.functionCall.args) lines.push(JSON.stringify(part.functionCall.args, null, 2));
        } else if (part.functionResponse) {
          lines.push(`[functionResponse] ${part.functionResponse.name}`);
          if (part.functionResponse.response) lines.push(JSON.stringify(part.functionResponse.response, null, 2));
          if (part._subAgentLog && part._subAgentLog.length > 0) {
            lines.push(`  [subAgentLog] ${part._subAgentLog.length} messages`);
            for (const sub of part._subAgentLog) {
              if (!sub) continue;
              lines.push(`    --- ${(sub.role || 'unknown').toUpperCase()} ---`);
              for (const sp of (sub.parts || [])) {
                if (sp.text) lines.push(`    [text] ${sp.text}`);
                else if (sp.functionCall) {
                  lines.push(`    [functionCall] ${sp.functionCall.name}`);
                  if (sp.functionCall.args) lines.push('    ' + JSON.stringify(sp.functionCall.args, null, 2).replace(/\n/g, '\n    '));
                } else if (sp.functionResponse) {
                  lines.push(`    [functionResponse] ${sp.functionResponse.name}`);
                  if (sp.functionResponse.response) lines.push('    ' + JSON.stringify(sp.functionResponse.response, null, 2).replace(/\n/g, '\n    '));
                }
              }
            }
          }
        } else if (part.inlineData) {
          lines.push(`[inlineData] ${part.inlineData.mimeType}, ${Math.round((part.inlineData.data?.length || 0) / 1024)}KB`);
        } else {
          lines.push(`[unknown] ${JSON.stringify(part, null, 2)}`);
        }
      }
      lines.push('');
    }
    navigator.clipboard.writeText(lines.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!chatHistory || chatHistory.length === 0) {
    return <div className="debug-log-empty text-muted p-3">No chat history entries yet.</div>;
  }

  const renderPart = (part, partIdx) => {
    if (part.text) {
      return (
        <div key={partIdx} className="debug-part debug-part-text">
          <span className="debug-part-label">text</span>
          <pre className="debug-part-content">{part.text}</pre>
        </div>
      );
    }
    if (part.functionCall) {
      const fc = part.functionCall;
      return (
        <div key={partIdx} className="debug-part debug-part-function-call">
          <span className="debug-part-label">functionCall</span>
          <div className="debug-part-content">
            <strong>{fc.name}</strong>
            {fc.args && (
              <pre className="debug-json">{JSON.stringify(fc.args, null, 2)}</pre>
            )}
          </div>
        </div>
      );
    }
    if (part.functionResponse) {
      const fr = part.functionResponse;
      return (
        <div key={partIdx} className="debug-part debug-part-function-response">
          <span className="debug-part-label">functionResponse</span>
          <div className="debug-part-content">
            <strong>{fr.name}</strong>
            {fr.response && (
              <pre className="debug-json">{JSON.stringify(fr.response, null, 2)}</pre>
            )}
          </div>
          {part._pdfExtraction && (
            <details className="debug-subagent-log">
              <summary className="debug-subagent-summary">
                <span className="debug-subagent-badge">PDF Extraction</span>
                {part._pdfExtraction.linkCount} link{part._pdfExtraction.linkCount !== 1 ? 's' : ''} found
              </summary>
              <div className="debug-subagent-entries">
                {part._pdfExtraction.links.map((link, linkIdx) => (
                  <div key={linkIdx} className="debug-entry debug-subagent-entry" style={{ padding: '4px 8px' }}>
                    <div><strong>Page {link.page}:</strong> <a href={link.url} target="_blank" rel="noopener noreferrer">{link.url}</a></div>
                    {link.displayText && <div className="text-muted" style={{ fontSize: '0.85em' }}>Display text: "{link.displayText}"</div>}
                  </div>
                ))}
              </div>
            </details>
          )}
          {part._attachmentsInfo && (
            <details className="debug-subagent-log">
              <summary className="debug-subagent-summary">
                <span className="debug-subagent-badge">Attachments</span>
                {part._attachmentsInfo.count} attachment{part._attachmentsInfo.count !== 1 ? 's' : ''} returned
              </summary>
              <div className="debug-subagent-entries">
                {part._attachmentsInfo.attachments.map((att, attIdx) => (
                  <div key={attIdx} className="debug-entry debug-subagent-entry" style={{ padding: '4px 8px' }}>
                    <div>
                      <strong>{att.name}</strong>
                      <span className="badge bg-secondary ms-2" style={{ fontSize: '0.75em' }}>{att.kind}</span>
                      {att.hasExtracted && <span className="badge bg-success ms-1" style={{ fontSize: '0.75em' }}>extracted</span>}
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.85em' }}>
                      ID: {att.id}
                      {att.extractedLinkCount > 0 && ` \u2022 ${att.extractedLinkCount} link${att.extractedLinkCount !== 1 ? 's' : ''} extracted`}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
          {part._moodBoardDebug && (
            <details className="debug-subagent-log">
              <summary className="debug-subagent-summary">
                <span className="debug-subagent-badge">Mood Board URLs</span>
                {part._moodBoardDebug.items.length} item{part._moodBoardDebug.items.length !== 1 ? 's' : ''}, {part._moodBoardDebug.extractedPdfLinkCount} PDF link{part._moodBoardDebug.extractedPdfLinkCount !== 1 ? 's' : ''} available
              </summary>
              <div className="debug-subagent-entries">
                {part._moodBoardDebug.items.map((item, itemIdx) => (
                  <div key={itemIdx} className="debug-entry debug-subagent-entry" style={{ padding: '4px 8px' }}>
                    <div><strong>{item.name}</strong></div>
                    <div className="text-muted" style={{ fontSize: '0.85em' }}>
                      Agent URL: {item.agentUrl || <em>(empty)</em>}
                    </div>
                    <div style={{ fontSize: '0.85em', color: item.finalUrl ? 'inherit' : '#dc3545' }}>
                      Final URL: {item.finalUrl || <em>(none)</em>}
                      {item.corrected && <span className="badge bg-warning ms-1" style={{ fontSize: '0.7em' }}>corrected</span>}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
          {part._subAgentLog && part._subAgentLog.length > 0 && (
            <details className="debug-subagent-log">
              <summary className="debug-subagent-summary">
                <span className="debug-subagent-badge">{part._subAgentType || 'SubAgent'}</span>
                {part._subAgentLog.length} message{part._subAgentLog.length !== 1 ? 's' : ''}
              </summary>
              <div className="debug-subagent-entries">
                {part._subAgentLog.map((subEntry, subIdx) => {
                  if (!subEntry) return null;
                  const subRole = subEntry.role || 'unknown';
                  const subParts = subEntry.parts || [];
                  return (
                    <div key={subIdx} className={`debug-entry debug-entry-${subRole} debug-subagent-entry`}>
                      <div className="debug-entry-header">
                        <span className={`debug-role debug-role-${subRole}`}>{subRole}</span>
                        <span className="debug-index">#{subIdx}</span>
                        {subParts.length > 0 && (
                          <span className="debug-parts-count">{subParts.length} part{subParts.length !== 1 ? 's' : ''}</span>
                        )}
                      </div>
                      <div className="debug-entry-parts">
                        {subParts.map((subPart, subPartIdx) => renderPart(subPart, subPartIdx))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </details>
          )}
        </div>
      );
    }
    if (part.inlineData) {
      return (
        <div key={partIdx} className="debug-part debug-part-inline-data">
          <span className="debug-part-label">inlineData</span>
          <span className="debug-part-content text-muted">[{part.inlineData.mimeType}, {Math.round((part.inlineData.data?.length || 0) / 1024)}KB]</span>
        </div>
      );
    }
    // Fallback for unknown part types
    return (
      <div key={partIdx} className="debug-part">
        <span className="debug-part-label">unknown</span>
        <pre className="debug-part-content">{JSON.stringify(part, null, 2)}</pre>
      </div>
    );
  };

  return (
    <div className="debug-log-view">
      <div className="debug-log-toolbar">
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handleCopyAll}
          title="Copy all messages"
        >
          <i className={`bi ${copied ? 'bi-check-lg' : 'bi-clipboard'}`}></i>{' '}
          {copied ? 'Copied' : 'Copy All'}
        </Button>
      </div>
      {chatHistory.map((entry, idx) => {
        if (!entry) return null;
        const role = entry.role || 'unknown';
        const parts = entry.parts || [];
        const meta = [];
        if (entry._agent) meta.push(`agent: ${entry._agent}`);
        if (entry._routing) meta.push(`routed: ${entry._routing.reason}${entry._routing.previousAgent ? ` (prev: ${entry._routing.previousAgent})` : ''}`);
        if (entry._attachmentIds) meta.push(`attachments: ${entry._attachmentIds.length}`);
        if (entry._generatedImages) meta.push(`generatedImages: ${entry._generatedImages.length}`);

        return (
          <div key={idx} className={`debug-entry debug-entry-${role}`}>
            <div className="debug-entry-header">
              <span className={`debug-role debug-role-${role}`}>{role}</span>
              <span className="debug-index">#{idx}</span>
              {parts.length > 0 && (
                <span className="debug-parts-count">{parts.length} part{parts.length !== 1 ? 's' : ''}</span>
              )}
              {meta.length > 0 && (
                <span className="debug-meta">{meta.join(', ')}</span>
              )}
            </div>
            {entry._routing && (
              <div className="debug-routing-info">
                <span className="debug-routing-task">{entry._routing.task}</span>
              </div>
            )}
            <div className="debug-entry-parts">
              {parts.map((part, partIdx) => renderPart(part, partIdx))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Welcome placeholder shown when there are no messages yet
function WelcomePlaceholder() {
  return (
    <Row className="mb-3 g-0 justify-content-start">
      <Col xs={11} md={10}>
        <div className="assistant-plain-message">
          <p>Let's start designing your space. Share a photo of it as it is today along with the changes you'd like to make.</p>
        </div>
      </Col>
    </Row>
  );
}

function ChatInterface({ uid, sessionId, initialMessages, initialTitle, sessionTitle, sessionState, projectEstimate = null, onTitleChange, onDocumentUpdate, onTodoUpdate, onSpaceContextUpdate, onWorkItemsUpdate, onSpaceAnalysisUpdate, spaceAnalysis = [], onDecisionItemsUpdate, onQAItemsUpdate, onScopeOfWorkUpdate, onMoodBoardUpdate, onSessionCreated, activeTab = 'chat', isAdmin = false, isDevMode = false, readOnly = false }) {
  // Messages from Firebase subscription
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [agentStatus, setAgentStatus] = useState('Plum is thinking...');
  const [titleGenerated, setTitleGenerated] = useState(!!initialTitle);
  const [pendingImages, setPendingImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [urlError, setUrlError] = useState('');
  const [isLoadingUrl, setIsLoadingUrl] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [shareImage, setShareImage] = useState(null);
  const [optimisticMessages, setOptimisticMessages] = useState([]);
  const [chatHistory, setChatHistory] = useState([]);
  const [attachmentsMap, setAttachmentsMap] = useState({});
  const [streamingText, setStreamingText] = useState('');
  const [debugMode, setDebugMode] = useState(false);
  const [leadHandoffSubmitting, setLeadHandoffSubmitting] = useState(false);
  const [leadHandoffError, setLeadHandoffError] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesAreaRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);
  const pendingBlobUrls = useRef([]);
  const [isAtBottom, setIsAtBottom] = useState(true);

  const isTemp = sessionId && sessionId.startsWith('temp_');

  // Build a lookup from image URL → { title, design_number } from spaceAnalysis
  // so generated images in chat can show captions even for existing sessions
  const spaceAnalysisImageMap = useMemo(() => {
    const map = {};
    let counter = 0;
    (spaceAnalysis || []).forEach(analysis => {
      if (analysis.analysis_type === 'generated_design' && analysis.image_url) {
        counter++;
        map[analysis.image_url] = {
          title: analysis.title || null,
          design_number: analysis.design_number || counter,
          room_type: analysis.room_type || null,
        };
      }
    });
    return map;
  }, [spaceAnalysis]);

  // Extract current state images for the social share dialog
  const currentStateImages = useMemo(() => {
    const images = [];
    (spaceAnalysis || []).forEach((analysis, idx) => {
      if (analysis.analysis_type === 'current_state') {
        (analysis.image_urls || []).forEach((url, urlIdx) => {
          images.push({
            id: `current-${analysis.id || idx}-${urlIdx}`,
            url,
            summary: analysis.summary,
            room_type: analysis.room_type,
            category: 'current',
          });
        });
      }
    });
    return images;
  }, [spaceAnalysis]);

  // Use chatHistory-based display if available, fall back to legacy messages
  const chatHistoryMessages = chatHistoryToDisplayMessages(chatHistory, attachmentsMap);
  const displayMessages = chatHistoryMessages.length > 0
    ? chatHistoryMessages
    : (optimisticMessages.length > 0 && messages.length === 0)
      ? optimisticMessages
      : messages;

  // Clear optimistic messages once Firebase subscription delivers real messages
  useEffect(() => {
    if (messages.length > 0 && optimisticMessages.length > 0) {
      setOptimisticMessages([]);
      // Revoke blob URLs that were deferred until optimistic messages are no longer displayed
      pendingBlobUrls.current.forEach(url => URL.revokeObjectURL(url));
      pendingBlobUrls.current = [];
    }
  }, [messages.length, optimisticMessages.length]);

  // Subscribe to messages from Firebase for real sessions
  useEffect(() => {
    if (!uid || !sessionId || isTemp) {
      setMessages([]);
      return;
    }

    console.debug('[ChatInterface] Subscribing to messages for session:', sessionId);
    const unsubscribe = subscribeToMessages(uid, sessionId, setMessages);

    return () => {
      console.debug('[ChatInterface] Unsubscribing from messages');
      unsubscribe();
    };
  }, [uid, sessionId, isTemp]);

  // Subscribe to chatHistory for server-side agent sessions
  useEffect(() => {
    if (!uid || !sessionId || isTemp) return;

    const unsubChatHistory = subscribeToChatHistory(uid, sessionId, (history) => {
      setChatHistory(history);
    });
    const unsubAttachments = subscribeToAttachments(uid, sessionId, (atts) => {
      setAttachmentsMap(atts);
    });
    const unsubStreaming = subscribeToStreamingResponse(uid, sessionId, (text) => {
      setStreamingText(text);
    });
    const unsubStatus = subscribeToAgentStatus(uid, sessionId, (status) => {
      if (status) setAgentStatus(status);
    });
    // Detect if the agent is still processing (e.g. client reconnected mid-processing)
    const unsubProcessing = subscribeToAgentProcessing(uid, sessionId, (processing) => {
      if (processing) {
        // Agent is still running server-side — show loading state
        // Stale flag safety: if startedAt is older than 10 minutes, ignore it
        // (Cloud Functions v2 max timeout is 540s)
        const isStale = processing.startedAt && (Date.now() - processing.startedAt > 600000);
        if (!isStale) {
          setIsLoading(true);
        }
      } else {
        // Agent finished — clear isLoading (server is the source of truth for processing state)
        setIsLoading((prev) => prev ? false : prev);
      }
    });

    return () => {
      unsubChatHistory();
      unsubAttachments();
      unsubStreaming();
      unsubStatus();
      unsubProcessing();
    };
  }, [uid, sessionId, isTemp]);

  const scrollToBottom = (smooth = true) => {
    const el = messagesAreaRef.current;
    if (!el) return;
    if (smooth) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else {
      el.scrollTop = el.scrollHeight;
    }
  };

  // Auto-scroll only when already at bottom, so user scrolling up isn't hijacked
  useEffect(() => {
    if (isAtBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, optimisticMessages, chatHistory, streamingText]);

  // Jump to bottom when the chat tab is (re)activated
  useEffect(() => {
    if (activeTab === 'chat') {
      // Wait for layout/paint before scrolling
      const id = requestAnimationFrame(() => scrollToBottom(false));
      return () => cancelAnimationFrame(id);
    }
  }, [activeTab]);

  const handleMessagesScroll = () => {
    const el = messagesAreaRef.current;
    if (!el) return;
    const threshold = 80;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
    setIsAtBottom(atBottom);
  };

  useEffect(() => {
    if (!isLoading) {
      inputRef.current?.focus();
    }
  }, [isLoading]);

  // Reset chat state when switching sessions
  const prevSessionIdRef = useRef(sessionId);
  // High-water mark for funnel event emission off chatHistory. Reset to 0
  // whenever the session changes so a session switch doesn't replay events
  // for already-seen entries from the new session's history.
  const funnelHistoryCursorRef = useRef(0);
  useEffect(() => {
    if (prevSessionIdRef.current !== sessionId) {
      const wasTempToReal = prevSessionIdRef.current?.startsWith('temp_') && sessionId && !sessionId.startsWith('temp_');
      console.debug('[ChatInterface] Session change:', prevSessionIdRef.current, '->', sessionId, wasTempToReal ? '(temp→real)' : '');
      setMessages([]);
      setChatHistory([]);
      setAttachmentsMap({});
      setStreamingText('');
      setTitleGenerated(!!initialTitle);
      setPendingImages([]);
      setInput('');
      funnelHistoryCursorRef.current = 0;
      // Preserve optimistic messages during temp→real session transition
      if (!wasTempToReal) {
        setOptimisticMessages([]);
      }
    }
    prevSessionIdRef.current = sessionId;
    inputRef.current?.focus();
  }, [sessionId, initialTitle]);

  // Funnel emission for events that originate from agent output: scan new
  // chatHistory entries (since the last cursor) and fire one funnel event per
  // matching entry. The high-water mark is per session so loading an existing
  // session's history on mount fires for every historical occurrence once.
  // That matches "saw the design/form" semantics on first view; revisiting
  // the same session in a new tab will re-fire — accept that as the cost of
  // keeping the implementation client-side and stateless.
  useEffect(() => {
    if (!chatHistory || chatHistory.length === 0) return;
    const startIdx = funnelHistoryCursorRef.current;
    if (startIdx >= chatHistory.length) return;
    const userFacingCalls = ['ask_user', 'show_form', 'request_lead_handoff'];
    for (let i = startIdx; i < chatHistory.length; i++) {
      const entry = chatHistory[i];
      if (!entry || entry.role !== 'model' || !Array.isArray(entry.parts)) continue;
      const functionCalls = entry.parts.filter(p => p.functionCall);
      const hasUserFacingCall = functionCalls.some(p =>
        userFacingCalls.includes(p.functionCall.name));
      const hasInternalCall = functionCalls.some(p =>
        !userFacingCalls.includes(p.functionCall.name));
      const isStandaloneUserFacing = hasUserFacingCall && !hasInternalCall;

      // design_generated (step 5): the agent attached generated images to a
      // model entry. Fires regardless of whether that entry also contained
      // internal tool calls — image generation succeeded either way.
      const generated = entry._generatedImages;
      if (Array.isArray(generated) && generated.length > 0) {
        trackFunnelEvent('design_generated', { image_count: generated.length });
      }

      // Only count form/lead surfaces when the entry is actually rendered to
      // the user (mirrors the gating in chatHistoryToDisplayMessages — mixed
      // batches are server-rejected and replayed standalone).
      if (isStandaloneUserFacing) {
        for (const part of functionCalls) {
          const name = part.functionCall?.name;
          if (name === 'show_form') {
            // form_generated (step 6): agent presented a structured form.
            trackFunnelEvent('form_generated', {
              form_type: part.functionCall?.args?.form_type || '',
            });
          } else if (name === 'request_lead_handoff') {
            // lead_form_started (step 7): contractor-sourcing card surfaced.
            trackFunnelEvent('lead_form_started');
          }
        }
      }
    }
    funnelHistoryCursorRef.current = chatHistory.length;
  }, [chatHistory]);

  // Maximum file size: 20MB
  const MAX_FILE_SIZE = 20 * 1024 * 1024;

  // Allowed MIME type prefixes and exact types
  const ALLOWED_FILE_TYPES = [
    'image/',           // All image types
    'application/pdf',  // PDF documents
    'text/',            // Plain text, CSV, HTML, etc.
    'application/msword',                                                      // .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',  // .docx
    'application/rtf',  // RTF documents
  ];

  const isAllowedFileType = (file) => {
    return ALLOWED_FILE_TYPES.some(allowed =>
      allowed.endsWith('/') ? file.type.startsWith(allowed) : file.type === allowed
    );
  };

  // Classify a file into a kind based on its MIME type
  const getFileKind = (file) => {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type === 'application/pdf') return 'pdf';
    return 'file';
  };

  // Handle file selection (images, PDFs, and other documents)
  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Filter for allowed file types and size
    const tooLarge = files.filter(f => f.size > MAX_FILE_SIZE);
    const unsupported = files.filter(f => f.size <= MAX_FILE_SIZE && !isAllowedFileType(f));
    const allowedFiles = files.filter(f => f.size <= MAX_FILE_SIZE && isAllowedFileType(f));

    if (tooLarge.length > 0 || unsupported.length > 0) {
      const msgs = [];
      if (tooLarge.length > 0) msgs.push(`${tooLarge.map(f => f.name).join(', ')} exceeded the 20MB limit`);
      if (unsupported.length > 0) msgs.push(`${unsupported.map(f => f.name).join(', ')} — unsupported file type (images and documents only)`);
      alert(msgs.join('\n'));
    }
    if (allowedFiles.length === 0) { e.target.value = ''; return; }

    // Create preview URLs for display
    const newImages = allowedFiles.map(file => {
      const kind = getFileKind(file);
      return {
        kind,
        file,
        preview: kind === 'image' ? URL.createObjectURL(file) : null,
        name: file.name,
        label: null, // User can label as 'current', 'inspiration', 'product', or 'mood_board'
      };
    });

    setPendingImages(prev => [...prev, ...newImages]);

    // Clear the input so the same file can be selected again
    e.target.value = '';
  };

  // Remove a pending image
  const removeImage = (index) => {
    setPendingImages(prev => {
      const updated = [...prev];
      // Only revoke if it's a blob URL (not an external URL or null)
      if (updated[index].preview && updated[index].preview.startsWith('blob:')) {
        URL.revokeObjectURL(updated[index].preview);
      }
      updated.splice(index, 1);
      return updated;
    });
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    if (isLoading) return;

    const files = Array.from(e.dataTransfer.files);

    if (files.length > 0) {
      const tooLarge = files.filter(f => f.size > MAX_FILE_SIZE);
      const unsupported = files.filter(f => f.size <= MAX_FILE_SIZE && !isAllowedFileType(f));
      const allowedFiles = files.filter(f => f.size <= MAX_FILE_SIZE && isAllowedFileType(f));

      if (tooLarge.length > 0 || unsupported.length > 0) {
        const msgs = [];
        if (tooLarge.length > 0) msgs.push(`${tooLarge.map(f => f.name).join(', ')} exceeded the 20MB limit`);
        if (unsupported.length > 0) msgs.push(`${unsupported.map(f => f.name).join(', ')} — unsupported file type (images and documents only)`);
        alert(msgs.join('\n'));
      }

      if (allowedFiles.length > 0) {
        const newImages = allowedFiles.map(file => {
          const kind = getFileKind(file);
          return {
            kind,
            file,
            preview: kind === 'image' ? URL.createObjectURL(file) : null,
            name: file.name,
            label: null,
          };
        });
        setPendingImages(prev => [...prev, ...newImages]);
      }
    }

    // Also check for dropped image URLs (from browser drag)
    const text = e.dataTransfer.getData('text/plain');
    if (text && isValidImageUrl(text)) {
      addImageFromUrl(text);
    }
  };

  // URL image upload helpers
  const isValidImageUrl = (url) => {
    try {
      const parsed = new URL(url);
      // Check if it looks like an image URL
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
      const hasImageExtension = imageExtensions.some(ext =>
        parsed.pathname.toLowerCase().endsWith(ext)
      );
      // Also allow URLs that might be image services
      const isImageService = parsed.hostname.includes('imgur') ||
        parsed.hostname.includes('unsplash') ||
        parsed.hostname.includes('cloudinary') ||
        parsed.hostname.includes('images') ||
        parsed.hostname.includes('pbs.twimg') ||
        parsed.hostname.includes('i.redd.it');
      return hasImageExtension || isImageService;
    } catch {
      return false;
    }
  };

  // Set label on a pending image
  const setImageLabel = (index, label) => {
    setPendingImages(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], label };
      return updated;
    });
  };

  const addImageFromUrl = async (url) => {
    // Create a pending image entry with the URL as preview
    const newImage = {
      kind: 'image',
      file: null, // No file object for URL images
      preview: url,
      name: url.split('/').pop() || 'image',
      isUrl: true,
      originalUrl: url,
      label: null,
    };
    setPendingImages(prev => [...prev, newImage]);
  };

  const handleUrlSubmit = async () => {
    const url = urlInput.trim();
    if (!url) {
      setUrlError('Please enter a URL');
      return;
    }

    // Basic URL validation
    try {
      new URL(url);
    } catch {
      setUrlError('Please enter a valid URL');
      return;
    }

    setIsLoadingUrl(true);
    setUrlError('');

    try {
      // Try to load as image first
      const img = new Image();
      img.crossOrigin = 'anonymous';

      try {
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = () => reject(new Error('Failed to load image'));
          img.src = url;
        });
        // Image loaded successfully, add it
        await addImageFromUrl(url);
      } catch {
        // Not an image — add as a link attachment
        let hostname;
        try { hostname = new URL(url).hostname; } catch { hostname = url; }
        const newLink = {
          kind: 'link',
          file: null,
          preview: null,
          name: hostname,
          isUrl: true,
          originalUrl: url,
          label: null,
        };
        setPendingImages(prev => [...prev, newLink]);
      }
      setUrlInput('');
      setShowUrlModal(false);
    } catch (error) {
      setUrlError('Could not process this URL. Please check the URL and try again.');
    } finally {
      setIsLoadingUrl(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if ((!input.trim() && pendingImages.length === 0) || isLoading) return;

    const userText = input.trim();
    const hasImages = pendingImages.length > 0;

    // Build image label annotations for the agent
    let imageLabelText = '';
    if (hasImages) {
      const labeledImages = pendingImages.filter(img => img.label);
      if (labeledImages.length > 0) {
        const labelDescriptions = pendingImages.map((img, i) => {
          if (!img.label) return null;
          const labelMap = {
            current: 'Current Space',
            inspiration: 'Inspiration',
            product: 'Product',
            mood_board: 'Mood Board',
          };
          return `[Image ${i + 1} labeled: ${labelMap[img.label] || img.label}]`;
        }).filter(Boolean);
        imageLabelText = labelDescriptions.join(' ');
      }
    }

    // Build file-type-aware default text when user uploads without typing a message
    let defaultFileText = '';
    if (hasImages && !userText && !imageLabelText) {
      const imageCount = pendingImages.filter(img => img.kind === 'image').length;
      const pdfCount = pendingImages.filter(img => img.kind === 'pdf').length;
      const fileCount = pendingImages.filter(img => img.kind === 'file').length;
      const linkCount = pendingImages.filter(img => img.kind === 'link').length;
      const parts = [];
      if (imageCount > 0) parts.push(`${imageCount} image${imageCount > 1 ? 's' : ''}`);
      if (pdfCount > 0) parts.push(`${pdfCount} PDF${pdfCount > 1 ? 's' : ''}`);
      if (fileCount > 0) parts.push(`${fileCount} file${fileCount > 1 ? 's' : ''}`);
      if (linkCount > 0) parts.push(`${linkCount} link${linkCount > 1 ? 's' : ''}`);
      const fileDesc = parts.join(' and ');
      defaultFileText = `I'm sharing ${fileDesc} for the project.`;
    }

    const userContent = userText
      ? (imageLabelText ? `${userText}\n\n${imageLabelText}` : userText)
      : (imageLabelText || defaultFileText);

    setInput('');
    const imagesToUpload = [...pendingImages];
    setPendingImages([]);
    setIsLoading(true);

    // Per-message event with rough composition for funnel slicing.
    const imageCount = imagesToUpload.filter(img => img.kind === 'image').length;
    const pdfCount = imagesToUpload.filter(img => img.kind === 'pdf').length;
    const fileCount = imagesToUpload.filter(img => img.kind === 'file').length;
    const linkCount = imagesToUpload.filter(img => img.kind === 'link').length;
    trackEvent('message_sent', {
      has_attachments: imagesToUpload.length > 0,
      attachment_count: imagesToUpload.length,
      image_count: imageCount,
      pdf_count: pdfCount,
      file_count: fileCount,
      link_count: linkCount,
      is_new_session: isTemp,
    });
    // Funnel step 3: every user message sent to the agent.
    trackFunnelEvent('message_sent', {
      has_attachments: imagesToUpload.length > 0,
      attachment_count: imagesToUpload.length,
      is_new_session: isTemp,
    });
    // Funnel step 4: one event per uploaded file (any kind) so the report
    // can count total uploads, not just sessions-with-an-upload.
    for (const img of imagesToUpload) {
      if (img.kind === 'link') continue;
      trackFunnelEvent('file_uploaded', { kind: img.kind || 'file' });
    }
    if (uid) {
      trackFirstTime('first_message_sent', `message:${uid}`);
      if (imageCount > 0) {
        trackFirstTime('first_image_uploaded', `image:${uid}`, { count: imageCount });
      }
    }
    if (isTemp) {
      trackEvent('new_session_first_message_sent', {
        has_attachments: imagesToUpload.length > 0,
      });
    }

    // Set upload status based on actual file types
    let uploadStatus = 'Plum is thinking...';
    if (hasImages) {
      const hasOnlyImages = imagesToUpload.every(img => img.kind === 'image');
      const hasOnlyPdfs = imagesToUpload.every(img => img.kind === 'pdf');
      if (hasOnlyImages) uploadStatus = 'Uploading images...';
      else if (hasOnlyPdfs) uploadStatus = 'Uploading documents...';
      else uploadStatus = 'Uploading files...';
    }
    setAgentStatus(uploadStatus);

    // Show user message optimistically during session creation
    if (isTemp) {
      const optimisticUserMsg = {
        role: 'user',
        content: userContent,
        timestamp: Date.now(),
      };
      if (imagesToUpload.length > 0) {
        optimisticUserMsg.images = JSON.stringify(
          imagesToUpload.map(img => ({
            url: img.preview || img.originalUrl || null,
            name: img.name,
            label: img.label || undefined,
            kind: img.kind,
            type: img.kind === 'pdf' ? 'application/pdf' : img.kind === 'link' ? 'link' : img.kind === 'file' ? (img.file?.type || 'application/octet-stream') : undefined,
          }))
        );
      }
      setOptimisticMessages([optimisticUserMsg]);
    }

    // Create session in Firebase on first user input
    let activeSessionId = sessionId;
    if (isTemp && uid) {
      try {
        const session = await createSession(uid);
        activeSessionId = session.id;
        onSessionCreated(activeSessionId);
      } catch (error) {
        console.error('Failed to create session:', error);
        setOptimisticMessages([]);
        setIsLoading(false);
        return;
      }
    }

    // Upload files to Firebase Storage and create attachment records in RTDB
    const attachmentIds = [];
    let imageUploadErrors = [];
    if (imagesToUpload.length > 0 && uid && activeSessionId) {
      const fileImages = imagesToUpload.filter(img => img.kind === 'image' && img.file);
      const pdfFiles = imagesToUpload.filter(img => img.kind === 'pdf');
      const genericFiles = imagesToUpload.filter(img => img.kind === 'file');
      const linkItems = imagesToUpload.filter(img => img.kind === 'link');
      const urlImages = imagesToUpload.filter(img => img.kind === 'image' && img.isUrl);

      // Upload local image files to Storage
      const uploadResults = await Promise.allSettled(
        fileImages.map(img => uploadImage(uid, activeSessionId, img.file))
      );
      for (let i = 0; i < uploadResults.length; i++) {
        if (uploadResults[i].status === 'fulfilled') {
          const result = uploadResults[i].value;
          const id = await saveAttachment(uid, activeSessionId, {
            kind: 'image', url: result.url, name: result.name,
            mimeType: result.type || 'image/jpeg', label: fileImages[i].label || null,
          });
          attachmentIds.push(id);
        } else {
          imageUploadErrors.push({ name: fileImages[i].name, error: uploadResults[i].reason?.message || 'Upload failed' });
        }
      }

      // Upload URL images to Firebase Storage so they have permanent, CORS-enabled URLs.
      const urlUploadResults = await Promise.allSettled(
        urlImages.map(async (img) => {
          const imgEl = new Image();
          imgEl.crossOrigin = 'anonymous';
          await new Promise((resolve, reject) => {
            imgEl.onload = resolve;
            imgEl.onerror = reject;
            imgEl.src = img.originalUrl;
          });
          const canvas = document.createElement('canvas');
          canvas.width = imgEl.naturalWidth;
          canvas.height = imgEl.naturalHeight;
          canvas.getContext('2d').drawImage(imgEl, 0, 0);
          const isPng = img.originalUrl.toLowerCase().includes('.png');
          const mimeType = isPng ? 'image/png' : 'image/jpeg';
          const blob = await new Promise(resolve => canvas.toBlob(resolve, mimeType, isPng ? undefined : 0.95));
          const fileName = img.name || 'image.jpg';
          const file = new File([blob], fileName, { type: mimeType });
          return uploadImage(uid, activeSessionId, file);
        })
      );
      for (let i = 0; i < urlUploadResults.length; i++) {
        if (urlUploadResults[i].status === 'fulfilled') {
          const result = urlUploadResults[i].value;
          const id = await saveAttachment(uid, activeSessionId, {
            kind: 'image', url: result.url, name: result.name,
            mimeType: result.type || 'image/jpeg', label: urlImages[i].label || null,
          });
          attachmentIds.push(id);
        } else {
          imageUploadErrors.push({ name: urlImages[i].name, error: urlUploadResults[i].reason?.message || 'Upload failed' });
        }
      }

      // Upload PDF files to Storage (no client-side extraction — agent handles it)
      const pdfUploadResults = await Promise.allSettled(
        pdfFiles.map(img => uploadFile(uid, activeSessionId, img.file))
      );
      for (let i = 0; i < pdfUploadResults.length; i++) {
        if (pdfUploadResults[i].status === 'fulfilled') {
          const result = pdfUploadResults[i].value;
          const id = await saveAttachment(uid, activeSessionId, {
            kind: 'pdf', url: result.url, name: result.name,
            mimeType: 'application/pdf', label: pdfFiles[i].label || null,
          });
          attachmentIds.push(id);
        } else {
          imageUploadErrors.push({ name: pdfFiles[i].name, error: pdfUploadResults[i].reason?.message || 'Upload failed' });
        }
      }

      // Upload generic files to Storage
      const genericUploadResults = await Promise.allSettled(
        genericFiles.map(img => uploadFile(uid, activeSessionId, img.file))
      );
      for (let i = 0; i < genericUploadResults.length; i++) {
        if (genericUploadResults[i].status === 'fulfilled') {
          const result = genericUploadResults[i].value;
          const id = await saveAttachment(uid, activeSessionId, {
            kind: 'file', url: result.url, name: result.name,
            mimeType: result.type || 'application/octet-stream', label: genericFiles[i].label || null,
          });
          attachmentIds.push(id);
        } else {
          imageUploadErrors.push({ name: genericFiles[i].name, error: genericUploadResults[i].reason?.message || 'Upload failed' });
        }
      }

      // Save link attachments directly (no upload needed)
      for (const img of linkItems) {
        const id = await saveAttachment(uid, activeSessionId, {
          kind: 'link', url: img.originalUrl, name: img.name,
          mimeType: null, label: img.label || null,
        });
        attachmentIds.push(id);
      }

      // Defer blob URL revocation if optimistic messages reference these previews
      const localFileImages = imagesToUpload.filter(img => img.file && img.kind === 'image');
      if (isTemp) {
        localFileImages.forEach(img => {
          if (img.preview && img.preview.startsWith('blob:')) {
            pendingBlobUrls.current.push(img.preview);
          }
        });
      } else {
        localFileImages.forEach(img => {
          if (img.preview) URL.revokeObjectURL(img.preview);
        });
      }

      if (attachmentIds.length > 0) {
        setAgentStatus('Analyzing files...');
      }
    }

    let clientProcessingSet = false;
    try {
      // Generate title after first user message
      if (!titleGenerated) {
        setTitleGenerated(true);
        callAgentGenerateTitle(userText).then((title) => {
          updateSessionTitle(uid, activeSessionId, title);
          onTitleChange(title);
        }).catch(() => {});
      }

      // Write user message to chatHistory immediately so it appears in the UI
      // via the real-time subscription — must complete before calling the Cloud Function
      // so the server reads the correct chatHistory
      const userHistoryEntry = { role: 'user', parts: [{ text: userContent }] };
      if (attachmentIds.length > 0) {
        userHistoryEntry._attachmentIds = attachmentIds;
      }
      await appendUserChatEntry(uid, activeSessionId, userHistoryEntry);

      // Write the durable processing flag BEFORE invoking the callable so the
      // loading state survives page refreshes and cold-start delay. The server
      // overwrites this on entry to sendMessage and clears it on completion.
      await setAgentProcessing(uid, activeSessionId, { startedAt: Date.now(), source: 'client' });
      clientProcessingSet = true;

      // Call server-side agent — it writes all state updates directly to RTDB
      // Client sees updates via existing real-time subscriptions
      await callAgentSendMessage(
        activeSessionId,
        userContent,
        attachmentIds
      );

      // Handle image upload errors (client-side only)
      if (imageUploadErrors.length > 0) {
        const errorList = imageUploadErrors.map(e => `- ${e.name}: ${e.error}`).join('\n');
        await saveMessage(uid, activeSessionId, {
          role: 'assistant',
          content: `**Note:** Some images could not be processed:\n${errorList}\n\nYou can try re-uploading the failed images.`,
        });
      }
    } catch (error) {
      console.error('Agent error:', error);
      // Error display is handled server-side — the cloud function writes error
      // entries to chatHistory so the client sees them via the real-time subscription.
      // For network errors, the agent may still be running server-side and the
      // agentProcessing subscription will handle the loading state when it completes.
      if (error.code === 'functions/resource-exhausted') {
        // Server enforces usage caps via checkUsageLimits — surface the cap as
        // its own conversion-style event so we can measure how often free
        // users actually hit the wall.
        trackEvent('usage_limit_reached', { kind: 'message' });
      } else if (error.name === 'AbortError' || error.message?.includes('fetch') || error.code === 'functions/cancelled') {
        console.warn('Lost connection to agent function — it may still be processing server-side');
      } else {
        trackEvent('agent_error', { code: error.code || 'unknown' });
        // Mix the Firebase error code into the message so the server-side
        // fingerprint groups by failure type (e.g. functions/internal vs
        // functions/deadline-exceeded) — by default both arrive with the same
        // generic stack from the callable wrapper. The server-side handler
        // (functions/index.js → describeAgentError) prefixes its HttpsError
        // message with [stage origin] when wrapping a non-HttpsError, so the
        // final logged form is "[agent functions/internal] [agent-loop
        // GoogleGenAIError/INTERNAL] <body>" — actionable rather than the
        // old opaque "[agent functions/internal] internal".
        const code = error?.code || 'unknown';
        // If the server message is missing/generic but details are attached,
        // synthesise a prefix so old-style "internal" reports still split
        // by stage rather than collapsing onto a single fingerprint.
        const baseMessage = error?.message || 'Agent error';
        const d = error?.details;
        const hasStagePrefix = typeof baseMessage === 'string' && baseMessage.startsWith('[');
        const synthesizedTag = !hasStagePrefix && d && typeof d === 'object'
          ? `[${[d.stage, [d.name, d.code].filter(Boolean).join('/')].filter(Boolean).join(' ')}] `
          : '';
        const wrapped = new Error(`[agent ${code}] ${synthesizedTag}${baseMessage}`);
        if (error?.stack) wrapped.stack = error.stack;
        logError(wrapped, { source: 'agent_error' });
      }
      if (error.code !== 'functions/cancelled' && error.name !== 'AbortError' && !error.message?.includes('fetch') && clientProcessingSet) {
        // Callable rejected before the server cleared the flag — drop the client's
        // marker so the spinner doesn't hang forever.
        try { await clearAgentProcessing(uid, activeSessionId); } catch (e) { /* ignore */ }
      }
    } finally {
      // Only clear isLoading if the agent isn't still processing server-side.
      // The agentProcessing subscription will clear it when the server finishes.
      setIsLoading(false);
    }
  };

  // Handle form submission - sends form data back to the agent
  const handleFormSubmit = async (formValues, formTitle, formMetadata = {}, messageKey = null) => {
    if (isLoading || !uid || !sessionId) return;

    // Format form submission as a readable message
    const labelMap = formMetadata.labelMap || {};
    let formattedMessage = `**Form Response: ${formTitle}**\n\n`;
    if (formValues._empty) {
      formattedMessage += 'No changes selected.';
    } else {
      for (const [key, value] of Object.entries(formValues)) {
        const label = labelMap[key] || key;
        formattedMessage += `- **${label}**: ${value}\n`;
      }
    }

    setIsLoading(true);
    setAgentStatus('Processing your selections...');

    let clientProcessingSet = false;
    try {
      // Optimistic work-item spec update. Isolated in its own try so a mutation
      // error (e.g. frozen state) never aborts the submission to the agent.
      if (formMetadata.work_item_id && formMetadata.specMappings && Object.keys(formMetadata.specMappings).length > 0 && !formValues._empty) {
        try {
          const workItemId = formMetadata.work_item_id;
          const currentWorkItems = sessionState?.workItems || [];
          const workItemIndex = currentWorkItems.findIndex(item => item.id === workItemId);
          if (workItemIndex >= 0) {
            const workItem = currentWorkItems[workItemIndex];
            const existingSpecs = (workItem.specifications || []).map(s => ({ ...s }));
            const newSpecs = [];
            for (const [formFieldId, value] of Object.entries(formValues)) {
              const mapping = formMetadata.specMappings[formFieldId];
              if (mapping && value) {
                const existingIndex = existingSpecs.findIndex(s => s.key === mapping.key);
                if (existingIndex >= 0) {
                  existingSpecs[existingIndex] = { ...existingSpecs[existingIndex], value };
                } else {
                  newSpecs.push({ key: mapping.key, label: mapping.label, value });
                }
              }
            }
            const updatedWorkItems = currentWorkItems.map((item, idx) =>
              idx === workItemIndex
                ? { ...item, specifications: [...existingSpecs, ...newSpecs], updatedAt: Date.now() }
                : item
            );
            onWorkItemsUpdate(updatedWorkItems);
          }
        } catch (e) {
          console.warn('Work item spec update failed; continuing with submission', e);
        }
      }

      // Write the durable processing flag BEFORE invoking the callable so the
      // loading state survives page refreshes and cold-start delay. The server
      // overwrites this on entry to sendMessage and clears it on completion.
      await setAgentProcessing(uid, sessionId, { startedAt: Date.now(), source: 'client' });
      clientProcessingSet = true;

      // Persist submitted values on the form message so it survives re-renders.
      // messageKey is typically undefined because dynamic_form entries are
      // synthesized from chatHistory without a message key, so this is a no-op
      // in the common case — kept for callers that do provide a key.
      if (messageKey) {
        await updateMessage(uid, sessionId, messageKey, {
          submittedValues: JSON.stringify(formValues),
        });
      }

      await saveMessage(uid, sessionId, { role: 'user', content: formattedMessage });

      // Re-key formValues using labels so the agent sees human-readable field names
      const labeledValues = {};
      for (const [key, value] of Object.entries(formValues)) {
        const label = labelMap[key] || key;
        labeledValues[label] = value;
      }
      const agentMessage = `Form submission for "${formTitle}":\n\`\`\`json\n${JSON.stringify(labeledValues, null, 2)}\n\`\`\``;

      // Write user message to chatHistory so the server reads it.
      // Include _displayText so the UI shows a human-friendly version
      // instead of the raw JSON that the agent sees.
      await appendUserChatEntry(uid, sessionId, {
        role: 'user',
        parts: [{ text: agentMessage }],
        _displayText: formattedMessage,
      });

      await callAgentSendMessage(sessionId, agentMessage);
    } catch (error) {
      console.error('Form submission error:', error);
      // If the server never took over (callable rejected before sendMessage
      // cleared the flag), drop the client's processing marker so the spinner
      // doesn't hang. Swallow errors from the clear itself.
      if (clientProcessingSet) {
        try { await clearAgentProcessing(uid, sessionId); } catch (e) { /* ignore */ }
      }
      // Surface the failure to the user. The cloud function writes its own
      // error entry when it gets far enough to catch, but failures before that
      // point (network, auth, payload) leave nothing visible — so write here.
      try {
        await saveMessage(uid, sessionId, {
          role: 'assistant',
          content: `**Error submitting form:** ${error.message || 'Unknown error'}. Please try again.`,
        });
      } catch (e) { /* ignore — nothing more we can do */ }
    } finally {
      setIsLoading(false);
    }
  };

  // Resolves a paused request_lead_handoff agent call. The server validates,
  // persists the lead, appends the functionResponse + a model acknowledgement
  // to chatHistory, and clears pendingLeadHandoff. The chatHistory
  // subscription then re-renders the card in its submitted state — we don't
  // need to invoke agentSendMessage to resume.
  const handleLeadHandoffSubmit = async (values) => {
    if (!uid || !sessionId || leadHandoffSubmitting) return;
    setLeadHandoffSubmitting(true);
    setLeadHandoffError(null);
    try {
      await callSubmitLead({ sessionId, ...values });
      // Send a single lead_submitted GA event. trackEvent is a no-op without
      // gtag; Consent Mode v2 handles the denied-consent case. We send only a
      // coarse ZIP3 (no PII) so the GA report stays compliant.
      trackEvent('lead_submitted', {
        session_id: sessionId,
        project_type: values.projectType || '',
        budget_range: values.budgetRange || '',
        timeline: values.timeline || '',
        zip_prefix: (values.zip || '').slice(0, 3),
      });
      // Funnel step 8: lead form submitted successfully. Mirrors the
      // diagnostic event above with the same coarse-only fields so the
      // funnel report carries no PII.
      trackFunnelEvent('lead_form_submitted', {
        project_type: values.projectType || '',
        budget_range: values.budgetRange || '',
        timeline: values.timeline || '',
        zip_prefix: (values.zip || '').slice(0, 3),
      });
    } catch (error) {
      console.error('Lead submission error:', error);
      setLeadHandoffError(error.message || 'Could not submit your match request. Please try again.');
    } finally {
      setLeadHandoffSubmitting(false);
    }
  };

  const handleLeadHandoffSkip = async () => {
    if (!uid || !sessionId || leadHandoffSubmitting) return;
    setLeadHandoffSubmitting(true);
    setLeadHandoffError(null);
    try {
      await callCancelLeadHandoff(sessionId);
    } catch (error) {
      console.error('Lead skip error:', error);
      setLeadHandoffError(error.message || 'Could not save your choice. Please try again.');
    } finally {
      setLeadHandoffSubmitting(false);
    }
  };

  // Debug: log current messages state
  console.debug('[ChatInterface] Render - messages count:', messages.length, 'optimistic:', optimisticMessages.length, 'isTemp:', isTemp);

  const showMessages = activeTab === 'chat';

  return (
    <div
      className={`chat-container ${isDragging ? 'drag-active' : ''} ${activeTab !== 'chat' ? 'd-none' : ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {isDragging && (
        <div className="drag-overlay">
          <div className="drag-overlay-content">
            <i className="bi bi-cloud-arrow-up"></i>
            <span>Drop images here</span>
          </div>
        </div>
      )}
      {showMessages && (
      <div
        className="messages-area flex-grow-1 overflow-auto"
        ref={messagesAreaRef}
        onScroll={handleMessagesScroll}
      >
        {/* Debug mode toggle — dev admin only */}
        {isAdmin && isDevMode && (
          <div className="debug-toggle-bar">
            <Form.Check
              type="switch"
              id="debug-mode-toggle"
              label="Debug Log"
              checked={debugMode}
              onChange={(e) => setDebugMode(e.target.checked)}
              className="debug-toggle"
            />
          </div>
        )}
        {/* Debug log view replaces normal chat when active */}
        {debugMode && isAdmin && isDevMode ? (
          <DebugLogView chatHistory={chatHistory} />
        ) : (
        <>
        {/* Show welcome placeholder when no messages */}
        {displayMessages.length === 0 && !isLoading && <WelcomePlaceholder />}
        {displayMessages.map((msg, index) => {
          // Handle todo_update messages specially
          if (msg.role === 'todo_update') {
            // Parse content if it's a string (from Firebase)
            const items = safeJsonParse(msg.content, []);
            if (!Array.isArray(items) || items.length === 0) return null;
            return (
              <Row key={index} className="mb-3 g-0 justify-content-start">
                <Col xs={11} md={10}>
                  <div className="assistant-plain-message">
                    <TodoUpdateMessage items={items} />
                  </div>
                </Col>
              </Row>
            );
          }

          // Handle lead_handoff card
          if (msg.role === 'lead_handoff') {
            const prefill = safeJsonParse(msg.content, {}) || {};
            return (
              <Row key={index} className="mb-3 g-0 justify-content-start">
                <Col xs={11} md={10}>
                  <div className="assistant-plain-message">
                    <LeadHandoffCard
                      prefill={prefill}
                      onSubmit={handleLeadHandoffSubmit}
                      onSkip={handleLeadHandoffSkip}
                      disabled={isLoading || leadHandoffSubmitting}
                      submittedState={msg.submittedState || null}
                      errorMessage={leadHandoffError}
                    />
                  </div>
                </Col>
              </Row>
            );
          }

          // Handle dynamic_form messages
          if (msg.role === 'dynamic_form') {
            const formData = safeJsonParse(msg.content, null);
            if (!formData || !Array.isArray(formData.sections)) return null;
            const submittedValues = safeJsonParse(msg.submittedValues, null);
            return (
              <Row key={index} className="mb-3 g-0 justify-content-start">
                <Col xs={11} md={10}>
                  <div className="assistant-plain-message">
                    <DynamicForm
                      formData={formData}
                      onSubmit={(values, metadata) => handleFormSubmit(values, formData.title, metadata, msg._key)}
                      disabled={isLoading}
                      submittedValues={submittedValues}
                    />
                  </div>
                </Col>
              </Row>
            );
          }

          // Regular user/assistant messages
          // Parse images from Firebase (stored as JSON string) or use directly
          const parsedImages = msg.images ? safeJsonParse(msg.images, []) : [];
          const msgImages = Array.isArray(parsedImages) ? parsedImages : [];

          // Get generated images for assistant messages (stored as JSON string in Firebase)
          const parsedGenImages = msg.generatedImages ? safeJsonParse(msg.generatedImages, []) : [];
          const generatedImages = Array.isArray(parsedGenImages) ? parsedGenImages : [];

          return (
            <Row
              key={index}
              className={`mb-3 g-0 ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
            >
              <Col xs={11} md={10}>
                {msg.role === 'user' ? (
                  <div className="message-bubble user">
                    <div className="message-content">
                      {msgImages.length > 0 && (
                        <div className="message-images">
                          {msgImages.map((img, imgIdx) => {
                            const isPdf = img.kind === 'pdf' || img.type === 'application/pdf' || img.name?.toLowerCase().endsWith('.pdf');
                            const isLink = img.kind === 'link' || img.type === 'link';
                            const isGenericFile = img.kind === 'file';
                            if (isPdf || isLink || isGenericFile) {
                              const icon = isPdf ? 'bi-file-earmark-pdf-fill' : isLink ? 'bi-link-45deg' : 'bi-file-earmark-fill';
                              const fallbackName = isPdf ? 'PDF' : isLink ? 'Link' : 'File';
                              const itemClass = isPdf ? 'pdf-item' : isLink ? 'link-item' : 'file-item';
                              return (
                                <div key={imgIdx} className={`message-image-item file-thumbnail ${itemClass}`}>
                                  <div className="file-thumbnail-content" onClick={() => img.url && window.open(img.url, '_blank')}>
                                    <i className={`bi ${icon}`}></i>
                                    <small>{img.name || fallbackName}</small>
                                  </div>
                                </div>
                              );
                            }
                            return (
                              <div key={imgIdx} className={`message-image-item ${img.pending ? 'pending' : ''}`}>
                                <img
                                  src={img.url}
                                  alt={img.name || 'Uploaded image'}
                                  onClick={() => !img.pending && setFullscreenImage({ url: img.url, name: img.name })}
                                />
                                {img.pending && (
                                  <div className="image-upload-overlay">
                                    <Spinner animation="border" size="sm" />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {msg.content && typeof msg.content === 'string' && <ReactMarkdown>{msg.content}</ReactMarkdown>}
                    </div>
                  </div>
                ) : (
                  <div className="assistant-plain-message">
                    {msgImages.length > 0 && (
                      <div className="message-images">
                        {msgImages.map((img, imgIdx) => {
                          const isPdf = img.type === 'application/pdf' || img.name?.toLowerCase().endsWith('.pdf');
                          const isLink = img.type === 'link';
                          if (isPdf || isLink) {
                            return (
                              <div key={imgIdx} className={`message-image-item file-thumbnail ${isPdf ? 'pdf-item' : 'link-item'}`}>
                                <div className="file-thumbnail-content" onClick={() => img.url && window.open(img.url, '_blank')}>
                                  <i className={`bi ${isPdf ? 'bi-file-earmark-pdf-fill' : 'bi-link-45deg'}`}></i>
                                  <small>{img.name || (isPdf ? 'PDF' : 'Link')}</small>
                                </div>
                              </div>
                            );
                          }
                          return (
                            <div key={imgIdx} className={`message-image-item ${img.pending ? 'pending' : ''}`}>
                              <img
                                src={img.url}
                                alt={img.name || 'Uploaded image'}
                                onClick={() => !img.pending && setFullscreenImage({ url: img.url, name: img.name })}
                              />
                              {img.pending && (
                                <div className="image-upload-overlay">
                                  <Spinner animation="border" size="sm" />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {msg.content && typeof msg.content === 'string' && (
                      <ReactMarkdown
                        components={generatedImages.length > 0 ? {
                          img: ({ src, alt, ...props }) => {
                            // Skip images whose URL matches a generated image to avoid duplicates
                            if (generatedImages.some(g => g.url === src)) return null;
                            return <img src={src} alt={alt} {...props} />;
                          }
                        } : undefined}
                      >{msg.content}</ReactMarkdown>
                    )}
                    {generatedImages.length > 0 && (
                      <div className="generated-images mt-3">
                        {generatedImages.map((img, imgIdx) => {
                          // Enrich with spaceAnalysis data for existing sessions
                          const sa = spaceAnalysisImageMap[img.url] || {};
                          const title = img.title || sa.title;
                          const designNumber = img.design_number || sa.design_number;
                          const roomType = img.room_type || sa.room_type;
                          return (
                            <div key={imgIdx} className="generated-image-item">
                              <div className="generated-image-wrapper">
                                <img
                                  src={img.url}
                                  alt={title || img.prompt || 'Generated design'}
                                  onClick={() => setFullscreenImage({ url: img.url, name: title, design_number: designNumber, room_type: roomType })}
                                />
                                <Button
                                  variant="light"
                                  size="sm"
                                  className="image-share-btn"
                                  onClick={(e) => { e.stopPropagation(); setShareImage({ url: img.url, title, design_number: designNumber, room_type: roomType }); }}
                                  title="Share design"
                                >
                                  <i className="bi bi-share"></i>
                                </Button>
                              </div>
                              {(title || designNumber || roomType) && (
                                <small className="text-muted d-block mt-1">
                                  {designNumber && title
                                    ? `Design #${designNumber}: ${title}`
                                    : title || (designNumber ? `Design #${designNumber}` : roomType)}
                                </small>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </Col>
            </Row>
          );
        })}
        {streamingText && isLoading && (
          <Row className="mb-3 g-0 justify-content-start">
            <Col xs={11} md={10}>
              <div className="assistant-plain-message">
                <ReactMarkdown>{streamingText}</ReactMarkdown>
              </div>
            </Col>
          </Row>
        )}
        {isLoading && (
          <Row className="mb-3 g-0 justify-content-start">
            <Col xs={11} md={10}>
              <div className="assistant-plain-message message-content agent-status">
                <Spinner animation="grow" size="sm" className="me-2" />
                <span className="status-text">{agentStatus}</span>
              </div>
            </Col>
          </Row>
        )}
        </>
        )}
        <div ref={messagesEndRef} />
      </div>
      )}

      {showMessages && !isAtBottom && (
        <button
          type="button"
          className="scroll-to-bottom-btn"
          onClick={() => scrollToBottom(true)}
          title="Scroll to latest"
          aria-label="Scroll to latest"
        >
          <i className="bi bi-arrow-down"></i>
        </button>
      )}

      {showMessages && readOnly && (
        <div className="input-area sticky-input">
          <div className="text-muted small text-center py-2">
            <i className="bi bi-eye me-1"></i>
            Read-only view — sending is disabled
          </div>
        </div>
      )}
      {showMessages && !readOnly && (
      <div className="input-area sticky-input">
        {/* Pending images preview */}
        {pendingImages.length > 0 && (
          <div className="pending-images-preview mb-2">
            {pendingImages.map((img, index) => (
              <div key={index} className={`pending-image-item ${img.isUrl ? 'url-image' : ''} ${img.kind === 'pdf' ? 'pdf-item' : ''} ${img.kind === 'file' ? 'file-item' : ''} ${img.kind === 'link' ? 'link-item' : ''} ${img.label ? `labeled-${img.label}` : ''}`}>
                {img.kind === 'link' ? (
                  <div className="pending-link-item">
                    <i className="bi bi-link-45deg"></i>
                    <small>{img.name}</small>
                  </div>
                ) : img.kind === 'pdf' ? (
                  <div className="pdf-preview-placeholder">
                    <i className="bi bi-file-earmark-pdf-fill"></i>
                    <small>{img.name}</small>
                  </div>
                ) : img.kind === 'file' ? (
                  <div className="pdf-preview-placeholder">
                    <i className="bi bi-file-earmark-fill"></i>
                    <small>{img.name}</small>
                  </div>
                ) : (
                  <img src={img.preview} alt={img.name} />
                )}
                <button
                  type="button"
                  className="remove-image-btn"
                  onClick={() => removeImage(index)}
                  disabled={isLoading}
                >
                  <i className="bi bi-x-circle-fill"></i>
                </button>
                <Dropdown className="image-label-dropdown" onClick={(e) => e.stopPropagation()}>
                  <Dropdown.Toggle
                    variant={img.label ? 'primary' : 'light'}
                    size="sm"
                    className="image-label-btn"
                    disabled={isLoading}
                  >
                    {img.label === 'current' && <><i className="bi bi-house-fill me-1"></i>Space</>}
                    {img.label === 'inspiration' && <><i className="bi bi-stars me-1"></i>Inspo</>}
                    {img.label === 'product' && <><i className="bi bi-box-seam me-1"></i>Product</>}
                    {img.label === 'mood_board' && <><i className="bi bi-palette me-1"></i>Mood Board</>}
                    {!img.label && <><i className="bi bi-tag me-1"></i>Label</>}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => setImageLabel(index, 'current')}>
                      <i className="bi bi-house me-2"></i>Current Space
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => setImageLabel(index, 'inspiration')}>
                      <i className="bi bi-stars me-2"></i>Inspiration
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => setImageLabel(index, 'product')}>
                      <i className="bi bi-box-seam me-2"></i>Product / Material
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => setImageLabel(index, 'mood_board')}>
                      <i className="bi bi-palette me-2"></i>Mood Board
                    </Dropdown.Item>
                    {img.label && (
                      <>
                        <Dropdown.Divider />
                        <Dropdown.Item onClick={() => setImageLabel(index, null)}>
                          <i className="bi bi-x me-2"></i>Remove Label
                        </Dropdown.Item>
                      </>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            ))}
          </div>
        )}
        {(() => {
          const est = projectEstimate;
          if (!est) return null;
          const fmt = (n) =>
            typeof n === 'number' && !isNaN(n)
              ? '$' + Math.round(n).toLocaleString('en-US')
              : null;
          const lo = fmt(est.cost_low);
          const hi = fmt(est.cost_high);
          let costText = '';
          if (lo && hi) costText = lo === hi ? lo : `${lo}–${hi}`;
          else costText = lo || hi || '';
          const timeText = est.timeline || '';
          if (!costText && !timeText) return null;
          return (
            <div
              className="budget-tracker"
              title="Live estimate based on the current scope — a planning guide, not a quote."
            >
              {costText && (
                <span className="budget-tracker-item">
                  <i className="bi bi-cash-coin me-1"></i>{costText}
                </span>
              )}
              {timeText && (
                <span className="budget-tracker-item">
                  <i className="bi bi-clock me-1"></i>{timeText}
                </span>
              )}
              <span className="budget-tracker-est">estimated</span>
            </div>
          );
        })()}
        <Form onSubmit={handleSubmit}>
          <Row className="g-2 align-items-end">
            <Col xs="auto" className="d-flex gap-1">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageSelect}
                accept="image/*,application/pdf,text/*,.doc,.docx,.rtf"
                multiple
                style={{ display: 'none' }}
              />
              <Dropdown drop="up" className="attach-dropdown">
                <Dropdown.Toggle
                  variant="outline-secondary"
                  className="image-upload-button"
                  disabled={isLoading}
                  title="Attach images, PDFs, or URLs"
                >
                  <i className="bi bi-paperclip"></i>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => fileInputRef.current?.click()}>
                    <i className="bi bi-image me-2"></i>Upload images or PDFs
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => setShowUrlModal(true)}>
                    <i className="bi bi-link-45deg me-2"></i>Add from URL
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Col>
            <Col>
              <Form.Control
                as="textarea"
                ref={inputRef}
                rows={2}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                disabled={isLoading}
                className="chat-input"
              />
            </Col>
            <Col xs="auto">
              <Button
                type="submit"
                variant="primary"
                disabled={isLoading || (!input.trim() && pendingImages.length === 0)}
                className="send-button"
              >
                <i className="bi bi-send-fill"></i>
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
      )}

      {/* URL Modal */}
      <Modal show={showUrlModal} onHide={() => { setShowUrlModal(false); setUrlInput(''); setUrlError(''); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add from URL</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Paste a URL to an image, PDF, or product page. Image URLs will show a preview; other links will be shared with the AI assistant.
          </p>
          <InputGroup>
            <Form.Control
              type="url"
              placeholder="https://example.com/..."
              value={urlInput}
              onChange={(e) => { setUrlInput(e.target.value); setUrlError(''); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleUrlSubmit();
                }
              }}
              isInvalid={!!urlError}
              disabled={isLoadingUrl}
              autoFocus
            />
            <Button
              variant="primary"
              onClick={handleUrlSubmit}
              disabled={isLoadingUrl || !urlInput.trim()}
            >
              {isLoadingUrl ? <Spinner animation="border" size="sm" /> : <i className="bi bi-plus-lg"></i>}
            </Button>
          </InputGroup>
          {urlError && (
            <Form.Text className="text-danger">{urlError}</Form.Text>
          )}
        </Modal.Body>
      </Modal>

      {/* Fullscreen Image Modal */}
      <Modal
        show={!!fullscreenImage}
        onHide={() => setFullscreenImage(null)}
        centered
        size="xl"
        className="fullscreen-image-modal"
      >
        <Modal.Header closeButton className="border-0 position-absolute top-0 end-0" style={{ zIndex: 1 }}>
        </Modal.Header>
        <Modal.Body className="p-0 d-flex align-items-center justify-content-center bg-dark">
          {fullscreenImage && (
            <img
              src={fullscreenImage.url}
              alt={fullscreenImage.name || 'Full size image'}
              className="fullscreen-modal-image"
              style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain' }}
            />
          )}
        </Modal.Body>
        {fullscreenImage && (
          <Modal.Footer className="bg-dark text-white border-0 justify-content-between">
            <div className="text-center flex-grow-1">
              {(fullscreenImage.name || fullscreenImage.design_number || fullscreenImage.room_type) && (
                <small className="text-uppercase d-block text-muted">
                  {fullscreenImage.design_number && fullscreenImage.name
                    ? `Design #${fullscreenImage.design_number}: ${fullscreenImage.name}`
                    : fullscreenImage.name || (fullscreenImage.design_number ? `Design #${fullscreenImage.design_number}` : fullscreenImage.room_type)}
                </small>
              )}
            </div>
            <Button
              variant="outline-light"
              size="sm"
              onClick={() => { setShareImage({ url: fullscreenImage.url, title: fullscreenImage.name, design_number: fullscreenImage.design_number, room_type: fullscreenImage.room_type }); setFullscreenImage(null); }}
            >
              <i className="bi bi-share me-1"></i>Share
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      {/* Social Share Dialog */}
      <SocialShareDialog
        show={!!shareImage}
        onHide={() => setShareImage(null)}
        image={shareImage}
        currentStateImages={currentStateImages}
        sessionTitle={sessionTitle}
        uid={uid}
        sessionId={sessionId}
      />
    </div>
  );
}

export default ChatInterface;
